import { useRef, type KeyboardEvent } from 'react';
import { chakra, HStack, Text } from '@chakra-ui/react';

/**
 * @useFor a **segmented control** — 2-5 mutually-exclusive options
 * presented as a pill row where exactly one is active at a time.
 * Filter pills (All / Emergency / Attention / Follow on the Protocols
 * list), activation-mode picker in ProtocolDrawer (Alert / Report),
 * and any small enum picker where a Select would feel heavy.
 *
 * Single option in a `ToggleGroup`. `disabled` is supported so callers
 * can dim invalid combinations (e.g. ProtocolDrawer disables the
 * `alert` activation mode when urgency is `follow`).
 *
 * **Do NOT use** for:
 *   - **3rd-tier sub-navigation** between views (e.g. People > Users /
 *     Roles / SSO). Use `TabMenu` instead. ToggleGroup is for
 *     *filtering / parameterizing* a single view; TabMenu is for
 *     *navigating between* views.
 *   - **Single binary on/off** — use `toggle` (`ToggleButton`).
 *   - **6+ options** — at that count it should be a `Select` or
 *     command-palette picker, not a pill row.
 *   - **Multi-select** — ToggleGroup is single-select by design;
 *     reach for `CheckboxGroup` or a chip-input pattern.
 *
 * See `libs/themes/aegix/CONVENTIONS.md` for the full pattern map.
 */
export interface ToggleOption {
  value: string;
  label: string;
  disabled?: boolean;
  'data-e2e'?: string;
}

export interface ToggleGroupConfig {
  options: ToggleOption[];
  value: string;
  onChange: (value: string) => void;
}

export interface ToggleGroupProps extends ToggleGroupConfig {
  /** When set, each option flex-grows equally to fill the container. */
  fillWidth?: boolean;
  /** Forwarded to the outer wrapper for a11y / test selectors. */
  'data-e2e'?: string;
  /**
   * Accessible name for the group. A `radiogroup` with no name is
   * announced as a bare group, so pass one unless the group is already
   * named by a visible heading via `aria-labelledby`.
   */
  'aria-label'?: string;
  /** Id of the element that names this group (e.g. a section title). */
  'aria-labelledby'?: string;
}

/**
 * Themed segmented control.
 *
 * The canonical segmented control: `TableOptions` uses it for the
 * Active/Inactive style page-tier filter and the per-protocol drawer's
 * urgency / activation-mode pickers use it too, so no surface
 * re-implements the look inline.
 *
 * Styling matches the rest of `@themes/aegix` — `surface.input`
 * background with `surface.secondary` for the active chip, subtle
 * elevation on the active state, 6px inner radius / 10px outer
 * radius. Inactive options are `text.tertiary`; active is
 * `text.primary` + 600 weight.
 *
 * Use this when you want a mutually-exclusive "pick one of N" picker
 * where N is small (typically 2-4) and the options fit on one line.
 * For larger sets, reach for Tabs or DataTable column filters.
 *
 * ## Accessibility
 *
 * Implements the WAI-ARIA APG **radio group** pattern:
 * `role="radiogroup"` on the wrapper, `role="radio"` + `aria-checked`
 * on each option, and a single roving tabstop (the checked option is
 * `tabIndex=0`, the rest `-1`) so Tab enters and leaves the whole
 * group in one hop. Left/Up and Right/Down move selection *and* focus
 * with wrap-around; Home/End jump to the first/last option; disabled
 * options are skipped. Space/Enter still activate the focused option
 * via native `<button>` semantics. Chords carrying Alt/Ctrl/Meta are
 * left to the browser (Alt+Arrow is Back/Forward, Ctrl+Home/End is
 * scroll-to-end), as is any key the group cannot act on. The focus ring
 * comes from the theme's global `*:focus-visible` rule — don't add a
 * local one.
 *
 * Not `aria-pressed` toggle buttons — the two models are not
 * interchangeable: `aria-pressed` describes N independent on/off buttons
 * and conveys no mutual exclusivity, which is exactly what a segmented
 * control *is*. Roving tabindex is also only legible to assistive tech
 * inside a container role that implies it (`radiogroup`, `toolbar`,
 * `tablist`); under plain toggle buttons it is noise. Consumers asserting
 * on the DOM should read `aria-checked`, not `aria-pressed`.
 */
export function ToggleGroup({
  options,
  value,
  onChange,
  fillWidth = false,
  'data-e2e': dataE2e,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: ToggleGroupProps) {
  // Keyed by option value, so a node survives the re-render that
  // `onChange` triggers — arrow-key focus lands on the right element
  // even though selection is controlled from outside.
  const optionRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Roving tabindex: exactly one option is tab-reachable. Normally the
  // checked one; if the checked value is disabled (or matches no
  // option at all) the first enabled option takes the tabstop so the
  // group stays reachable. When every option is disabled nothing gets
  // it.
  const checkedIndex = options.findIndex(
    (option) => option.value === value && !option.disabled,
  );
  const tabStopIndex =
    checkedIndex >= 0
      ? checkedIndex
      : options.findIndex((option) => !option.disabled);

  const select = (index: number) => {
    const option = options[index];
    if (!option || option.disabled) return;
    optionRefs.current[option.value]?.focus();
    if (option.value !== value) onChange(option.value);
  };

  /**
   * Index of the first enabled option `step` hops away from `from`,
   * wrapping; `-1` when nothing is selectable. Bounded by the option
   * count so an all-disabled group terminates.
   */
  const nextEnabledIndex = (from: number, step: number) => {
    let candidate = from;
    for (let hop = 0; hop < options.length; hop++) {
      candidate = (candidate + step + options.length) % options.length;
      if (!options[candidate].disabled) return candidate;
    }
    return -1;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>, index: number) => {
    const { key } = event;

    // `event.key` reports the bare key name, so Alt/Cmd+ArrowLeft (browser
    // Back) and Ctrl+Home/End (jump to top/bottom of document) are
    // indistinguishable from the plain keys here. Bail before claiming
    // them — otherwise the group swallows the chord *and* moves the
    // selection, which on a dirty form is a silent unrequested edit.
    // Shift is left unguarded: no conflicting binding on a button.
    if (event.altKey || event.ctrlKey || event.metaKey) return;

    const step =
      key === 'ArrowRight' || key === 'ArrowDown'
        ? 1
        : key === 'ArrowLeft' || key === 'ArrowUp'
        ? -1
        : 0;
    if (step === 0 && key !== 'Home' && key !== 'End') return;

    let target: number;
    if (key === 'Home' || key === 'End') {
      const enabled = options.flatMap((option, i) =>
        option.disabled ? [] : [i],
      );
      target = enabled.length
        ? key === 'Home'
          ? enabled[0]
          : enabled[enabled.length - 1]
        : -1;
    } else {
      target = nextEnabledIndex(index, step);
    }

    // Only claim the key once we know the group can act on it. An
    // all-disabled group must leave Arrow/Home/End to the page rather
    // than silently blocking the scroll behind it.
    if (target < 0) return;
    event.preventDefault();
    select(target);
  };

  return (
    <HStack
      role="radiogroup"
      aria-orientation="horizontal"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      bg="surface.input"
      p={1}
      borderRadius="10px"
      gap={1}
      width={fillWidth ? '100%' : undefined}
      data-e2e={dataE2e}
    >
      {options.map((option, index) => {
        const isActive = option.value === value;
        const isDisabled = !!option.disabled;
        return (
          <chakra.button
            type="button"
            key={option.value}
            ref={(node: HTMLButtonElement | null) => {
              optionRefs.current[option.value] = node;
            }}
            flex={fillWidth ? 1 : undefined}
            px={3}
            py={1}
            borderRadius="6px"
            cursor={isDisabled ? 'not-allowed' : 'pointer'}
            bg={isActive ? 'surface.secondary' : 'surface.input'}
            opacity={isDisabled ? 0.5 : 1}
            boxShadow={
              isActive
                ? '0px 2px 4px 0px rgba(14, 18, 27, 0.03), 0px 6px 10px 0px rgba(14, 18, 27, 0.06)'
                : 'none'
            }
            data-e2e={option['data-e2e']}
            onClick={() => {
              if (!isDisabled) onChange(option.value);
            }}
            onKeyDown={(event) => handleKeyDown(event, index)}
            role="radio"
            aria-checked={isActive}
            aria-disabled={isDisabled || undefined}
            tabIndex={!isDisabled && index === tabStopIndex ? 0 : -1}
            _hover={
              isActive || isDisabled
                ? {}
                : {
                    bg: 'surface.secondary',
                    '& .toggle-label': { color: 'text.primary' },
                  }
            }
            transition="background 150ms"
          >
            <Text
              className="toggle-label"
              fontSize="14px"
              fontWeight={isActive ? '600' : '400'}
              color={
                isDisabled
                  ? 'text.disabled'
                  : isActive
                  ? 'text.primary'
                  : 'text.tertiary'
              }
              letterSpacing="-0.006em"
              textAlign="center"
              transition="color 150ms"
              whiteSpace="nowrap"
              textTransform="capitalize"
            >
              {option.label}
            </Text>
          </chakra.button>
        );
      })}
    </HStack>
  );
}
